import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useRef, useEffect, useState } from "react";
import ModuleHeader from "./ModuleHeader";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import { eamApi } from "@/api/eamApi";
import { REM_CONFIG } from "@/utils/fontSize";
const AssetProject = ({ className = "" }) => {
    const [projects, setProjects] = useState([]);
    const projectNames = projects.map((item) => item.cname) || [];
    useEffect(() => {
        getData();
    }, []);
    const getData = async () => {
        try {
            const res = await eamApi.project();
            setProjects(res.data);
        }
        catch (error) {
            console.error("获取部门数据失败:", error);
            // 可以设置默认数据或错误处理
        }
    };
    const scrollConfig = {
        speed: 0.6,
        visibleCount: 4,
        loop: true,
        interval: 250,
    };
    const getTotalCount = () => projects.reduce((sum, p) => sum + p.count, 0);
    const getRatios = () => projects.map((p) => getTotalCount() > 0 ? Math.round((p.count / getTotalCount()) * 100) : 0);
    const getChartOption = () => ({
        dataZoom: [
            {
                type: "inside",
                xAxisIndex: 0,
                start: 0,
                end: projectNames.length > 0
                    ? (scrollConfig.visibleCount / projectNames.length) * 100
                    : 100,
                show: false,
                zoomLock: true,
                moveOnMouseWheel: false,
            },
        ],
        tooltip: {
            trigger: "axis",
            axisPointer: { type: "cross" },
            backgroundColor: "rgba(7, 31, 75, 0.8)",
            borderColor: "#446b9f",
            textStyle: { color: "#fff" },
            formatter: (params) => {
                let html = `${params[0].name}<br/>`;
                params.forEach((param) => {
                    const value = param.seriesName === "占比" ? `${param.value}%` : param.value;
                    html += `${param.marker} ${param.seriesName}: ${value}<br/>`;
                });
                return html;
            },
        },
        legend: {
            data: ["金额", "数量", "占比"],
            textStyle: { color: "#fff", fontSize: REM_CONFIG.fontSize.small },
            orient: "horizontal",
            left: "center",
            top: 3,
            itemGap: 15,
        },
        xAxis: {
            type: "category",
            data: projectNames,
            axisLabel: {
                color: "#fff",
                fontSize: REM_CONFIG.fontSize.small,
                formatter: (value) => {
                    const line1Len = 4;
                    const line2Len = 4;
                    let line1 = value.slice(0, line1Len);
                    let line2 = "";
                    if (value.length > line1Len) {
                        line2 = value.slice(line1Len, line1Len + line2Len);
                        if (value.length > line1Len + line2Len) {
                            line2 += "...";
                        }
                    }
                    return line1 + (line2 ? `\n${line2}` : "");
                },
                interval: 0,
                margin: 15,
            },
            axisLine: { lineStyle: { color: "#446b9f" } },
            axisTick: { show: false },
        },
        yAxis: [
            {
                type: "value",
                name: "金额（万元）",
                nameTextStyle: {
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                    align: "center",
                },
                axisLabel: {
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                    margin: 10,
                },
                splitLine: {
                    lineStyle: { color: "rgba(255,255,255,0.1)", type: "dashed" },
                },
                max: 5000,
            },
            {
                type: "value",
                name: "数量（项）",
                nameTextStyle: {
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                    align: "center",
                },
                axisLabel: {
                    color: "#fff",
                    fontSize: REM_CONFIG.fontSize.small,
                    formatter: "{value}",
                },
                splitLine: { show: false },
                max: 100,
            },
        ],
        series: [
            {
                name: "金额",
                type: "bar",
                yAxisIndex: 0,
                data: projects.map((p) => p.amount),
                barWidth: "14%",
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#2F96E2" },
                        { offset: 1, color: "#1A659B" },
                    ]),
                    borderRadius: [8, 8, 0, 0],
                },
            },
            {
                name: "数量",
                type: "bar",
                yAxisIndex: 1,
                data: projects.map((p) => p.count),
                barWidth: "14%",
                itemStyle: {
                    color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                        { offset: 0, color: "#60FFFF" },
                        { offset: 1, color: "#40BFFF" },
                    ]),
                    borderRadius: [8, 8, 0, 0],
                },
            },
            {
                name: "占比",
                type: "line",
                yAxisIndex: 1,
                data: getRatios(),
                itemStyle: { color: "#2F96E2" },
                symbol: "circle",
                symbolSize: 8,
                smooth: true,
                lineStyle: { width: 2 },
                label: {
                    show: true,
                    position: "top",
                    color: "#fff",
                    formatter: "{c}%",
                },
            },
        ],
        grid: {
            left: "4%",
            right: "4%",
            bottom: "0%",
            top: 60,
            containLabel: true,
        },
    });
    const chartRef = useRef(null);
    const timerRef = useRef(null);
    const startAutoScroll = (chart) => {
        if (timerRef.current)
            clearInterval(timerRef.current);
        // 如果没有数据或数据不足以滚动，则不启动轮播
        if (projectNames.length <= scrollConfig.visibleCount) {
            return;
        }
        let currentStart = 0;
        const totalCount = projectNames.length;
        const visiblePercent = (scrollConfig.visibleCount / totalCount) * 100;
        const maxStart = 100 - visiblePercent;
        timerRef.current = setInterval(() => {
            currentStart += scrollConfig.speed;
            if (scrollConfig.loop && currentStart >= maxStart) {
                currentStart = 0;
            }
            else if (!scrollConfig.loop && currentStart >= maxStart) {
                currentStart = maxStart;
                clearInterval(timerRef.current);
            }
            chart.dispatchAction({
                type: "dataZoom",
                start: currentStart,
                end: currentStart + visiblePercent,
                dataZoomIndex: 0,
            });
        }, scrollConfig.interval);
    };
    useEffect(() => {
        // 当数据加载完成后启动轮播
        if (projects.length > 0) {
            const chartInstance = chartRef.current?.getEchartsInstance();
            if (chartInstance) {
                // 确保图表渲染完成后再启动轮播
                setTimeout(() => {
                    startAutoScroll(chartInstance);
                }, 300);
            }
        }
        return () => {
            if (timerRef.current)
                clearInterval(timerRef.current);
        };
    }, [projects]); // 依赖projects数组，数据变化时重新启动轮播
    return (_jsxs("div", { className: `
        relative 
        bg-gradient-to-br from-blue-900/10 to-slate-900/20 
      
       
        flex flex-col 
        ${className}
      `, children: [_jsx(ModuleHeader, { title: "\u79D1\u7814\u9879\u76EE\u5206\u5E03" }), _jsx("div", { className: "flex-1", style: { minHeight: 0 }, children: _jsx(ReactEcharts, { ref: chartRef, option: getChartOption(), style: { width: "100%", height: "100%" }, opts: { renderer: "canvas" } }) })] }));
};
export default AssetProject;
